import React, { useEffect, useState, useMemo, useRef } from 'react';
import { SearchBox, Drawer, TabPanel, Tabs } from 'tea-component';
import { useDispatch, useIntl, useSelector, getLocale } from 'umi';
import SvgIcon from '@/pages/home/components/SvgIcon';

import GameTypeFilter from './components/GameTypeFilter';
import RelatedGame from './components/RelatedGame';
import AllGame from './components/AllGame';
// import NewGame from './components/NewGame';
import './index.scss';

import { GameSelectorProps } from './types';

const GameSelector = (props: GameSelectorProps) => {
  const { visible, setVisible, menuWidth, namespace = 'dashboard' } = props;

  const intl = useIntl();
  const { currentGame, recentList, followList, gameList, newGame } = useSelector((state: any) => {
    const {
      currentGame,
      gameList: gameListOrigin,
      followList: followListOrigin,
      recentList: recentListOrigin,
      leftMenuWidth,
    } = state[namespace];
    const isEN = getLocale() === 'en-US';
    // 最终显示的收藏列表
    const followList =
      followListOrigin?.map((game: any) => ({
        ...game,
        game_name: isEN ? game.game_name || game.game_cn_name : game.game_cn_name || game.game_name,
        game_cn_name: isEN ? game.game_cn_name : game.game_name,
      })) || [];
    // 最终显示的全部游戏
    const gameList =
      gameListOrigin?.map((hostGame: any) => {
        const followTarget = followList?.find((followItem: any) => followItem.game_code === hostGame.game_code);
        const game_list = hostGame?.game_list?.map((game: any) => ({
          ...game,
          game_name: isEN ? game.game_name || game.game_cn_name : game.game_cn_name || game.game_name,
          game_cn_name: isEN ? game.game_cn_name : game.game_name,
        }));
        return {
          ...hostGame,
          game_list,
          game_name: isEN ? hostGame.game_name || hostGame.game_cn_name : hostGame.game_cn_name || hostGame.game_name,
          game_cn_name: isEN ? hostGame.game_cn_name : hostGame.game_name,
          isFollow: Boolean(followTarget),
          followId: followTarget?.id,
          followSort: followTarget?.sort,
        };
      }) || [];
    // 最终显示的最新浏览
    const recentList =
      recentListOrigin?.map((game: any) => {
        const followTarget = followList?.find((followItem: any) => followItem.game_code === game.game_code);

        return {
          ...game,
          game_name: isEN ? game.game_name || game.game_cn_name : game.game_cn_name || game.game_name,
          game_cn_name: isEN ? game.game_cn_name : game.game_name,
          isFollow: Boolean(followTarget),
          followId: followTarget?.id,
          followSort: followTarget?.sort,
        };
      }) || [];
    const newGame =
      gameList
        ?.map((hostGame: any) => {
          const game_List = hostGame?.game_List?.filter((game: any) => game?.is_new_game);

          return {
            ...hostGame,
            game_List,
          };
        })
        ?.filter((game: any) => game?.game_List?.length > 0 || game?.is_new_game) || [];

    return {
      currentGame,
      gameList,
      recentList,
      followList,
      newGame,
      leftMenuWidth,
    };
  });
  // 是否首次进入
  const isFirst = useRef(true);
  const tabs = [
    {
      id: 'related',
      icon: 'uesr-heart',
      num: followList?.length + recentList?.length || 0,
      label: intl.formatMessage({ id: 'game_selector_title_game_related' }),
      component: (props: any = {}) => (
        <RelatedGame
          namespace={namespace}
          setGameModalVisible={props.setVisible}
          recentList={props.recentList}
          followList={props.followList}
          setActiveId={props.setActiveId}
          menuWidth={menuWidth}
        />
      ),
    },
    {
      id: 'all',
      icon: 'outline-gamepad',
      num: gameList?.length || 0,
      label: intl.formatMessage({ id: 'game_selector_title_game_all' }),
      component: (props: any = {}) => (
        <AllGame gameList={props.gameList} setGameModalVisible={props.setVisible} namespace={namespace} menuWidth={menuWidth} />
      ),
    },
    // {
    //   id: 'new',
    //   icon: 'flag-m',
    //   num: newGame?.length || 0,
    //   label: intl.formatMessage({ id: 'game_selector_title_game_new' }),
    //   component: (props: any = {}) => (
    //     <NewGame setGameModalVisible={props.setVisible} newGame={props.newGame} setActiveId={props.setActiveId} />
    //   ),
    // },
  ];
  const [searchValue, setSearchValue] = useState('');
  const isSearchMode = searchValue?.length > 0;
  const [activeId, setActiveId] = useState<string>(tabs[0].id);
  const dispatch = useDispatch();

  const searchGameList = useMemo(() => {
    if (!isSearchMode) return [];

    return gameList?.filter(
      (game: any) =>
        game.game_name?.toLowerCase()?.includes(searchValue?.toLowerCase()) ||
        game.game_cn_name?.toLowerCase()?.includes(searchValue?.toLowerCase()),
    );
  }, [gameList, searchValue, intl.locale]);

  const handleSearch = (searchValue: string) => {
    // 搜索模式下不选中任何 tab 页
    setSearchValue(searchValue);
  };

  const handleActiveChange = (id: any) => {
    // 查询模式下禁止切换
    if (isSearchMode) return;
    isFirst.current = false;
    setActiveId(id);
  };

  useEffect(() => {
    if (!visible || !currentGame?.game_code || activeId !== 'related') return;
    // 获取最近浏览和收藏的游戏
    dispatch({
      type: `${namespace}/getRecentAndFollowViewd`,
      payload: {
        game_code: currentGame?.game_code,
      },
    })
      .then(
        (isShowRelated) => {
          // 首次进入且 related 下无数据则展示所有游戏 tab
          if (isFirst.current && !isShowRelated && activeId === 'related') {
            setActiveId(tabs[1].id);
          }
        },
        () => {},
      )
      .finally(() => {});
  }, [visible, activeId, currentGame]);

  return (
    <Drawer
      className="drawer-left dtb-db-gameselector-wrap"
      style={{ left: `${menuWidth + 6}px` }}
      placement="left"
      // destroyOnClose={true}
      disableCloseIcon
      disableAnimation={false}
      outerClickClosable={true}
      showMask={false}
      visible={visible}
      onClose={() => {
        setVisible(false);
      }}
    >
      <div className="dbd-drawer-header">
        <SearchBox
          className="drawer-search"
          autoComplete="off"
          aria-autocomplete="none"
          placeholder={intl.formatMessage({ id: 'game_selector_search_text' })}
          onChange={(value) => handleSearch(value?.trim() || '')}
          onClear={() => handleSearch('')}
        />
        <Tabs
          style={{ display: searchValue ? 'none' : 'block' }}
          tabs={tabs}
          activeId={isSearchMode ? '' : activeId}
          tabBarRender={(children, tab: any) => (
            <div className="drawer-flex" onClick={() => handleActiveChange(tab.id)}>
              <SvgIcon className="drawer-flex-icon" type={tab.icon} />
              {children} ({tab.num})
            </div>
          )}
        />
      </div>
      {isSearchMode ? (
        <GameTypeFilter
          gameList={searchGameList}
          searchValue={searchValue}
          setGameModalVisible={setVisible}
          namespace={namespace}
          menuWidth={menuWidth}
        />
      ) : (
        tabs.map((tab) =>
          activeId == tab.id ? (
            <TabPanel id={tab.id} key={tab.id}>
              {tab.component({
                setVisible,
                recentList,
                followList,
                newGame,
                gameList,
                setActiveId,
              })}
            </TabPanel>
          ) : (
            ''
          ),
        )
      )}
    </Drawer>
  );
};
export default GameSelector;
